import { CustomEventWithDetail, html, View } from 'rune-ts';
import { makeSubScreenNavigate, PostMessageNavigate } from '../../../../shared/app/navigate';
import { preventEscape } from '../../../../shared/util/preventEscape';
import { Confirm } from '../Confirm/Confirm';
import klass from './ProductCardView.module.scss';
import { StarIcon } from '../../atoms/Icon';
import { classes } from '../../../../shared/util';
import {
  ProductCardViewData,
  ProductCardOption,
  ProductCardSize,
} from '../../../../features/ProductList/type';
import { ProductStoreName } from './view/ProductStoreName/ProductStoreName';
import { ProductLikeButton } from './view/ProductLikeButton/ProductLikeButton';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { pushLoginStack } from '@modules/Creator/Login/F/fs';
import { product_tag } from '../../../../features/ProductList/constant';
import { differenceInMilliseconds } from 'date-fns';
import { ProductCardHelper } from './ProductCardHelper';
import { ProductBadgeList } from '../ProductBadgeList/ProductBadgeList';
import { ProductCardPrice } from '../ProductCardPrice/ProductCardPrice';
import { dataStr } from '../../../../shared/util/dataStr';
import { ProductCardName } from './view/ProductCardName/ProductCardName';
import { ProductBadgeFactory } from '../ProductBadge/ProductBadgeFactory';
import { ProductBadgeBase } from '../ProductBadge/ProductBadgeBase';
import { ProductTagFactory } from '../ProductTag/ProductTagFactory';
import {
  ProductTagBase,
  ProductTagBaseProps,
  ProductTagOption,
  ProductTagRadius,
} from '../ProductTag/ProductTagBase';
import { ProductTagHelper } from '../ProductTag/utils/ProductTagHelper';
import { updateLikeMarppleShop } from '@modules/MShop/App/Product/Item/F/Function/updateLikeMarppleShop';
import { ProductCardStyler } from './utils/ProductCardStyler';
import { ProductCardVisibilityManager } from '@services/MarppleShop/renderApp/components/cells/ProductCard/utils/ProductCardVisibilityManager.ts';
import { isDate, isNumber, isUndefined } from '@fxts/core';
import { ProductBadgeVariant } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/strategies';
import { ProductBadgeType } from '@services/MarppleShop/features/ProductBadge/type';
import { ProductThumbnailView } from '@services/MarppleShop/renderApp/components/cells/ProductThumbnail/ProductThumbnailView.ts';

export class ProductLikeToggleEvent extends CustomEventWithDetail<{
  toggleLikeButton: () => void;
}> {}

export class ProductCardView extends View<ProductCardViewData> {
  state: {
    img_alt: string;
    post_message: PostMessageNavigate;
    stores_product_id: number;
  };

  LikeButton: ProductLikeButton | null;

  BadgeListView?: ProductBadgeList;

  RankingBadgeView?: ProductBadgeBase<number>;

  visibilityManager: ProductCardVisibilityManager;

  TagView?: ProductTagBase;

  private readonly size: ProductCardSize;

  private readonly styler: ProductCardStyler;

  private realtimeTagOption: ProductTagBaseProps;

  // 기간한정 상품인지
  readonly is_period_limited: boolean;

  // 수량한정 상품인지
  readonly is_quantity_limited: boolean;

  constructor(
    data: ProductCardViewData,
    private option: ProductCardOption,
  ) {
    super(
      {
        ...data,
        sell_start_at: data.sell_start_at ? new Date(data.sell_start_at) : null,
        sell_end_at: data.sell_end_at ? new Date(data.sell_end_at) : null,
        ordered_at: data.ordered_at ? new Date(data.ordered_at) : undefined,
      },
      option,
    );

    this.size = option.size;

    this.styler = new ProductCardStyler(this.size, data, option);

    this.visibilityManager = new ProductCardVisibilityManager(this.size, this.data, this.option);

    this.LikeButton = this.visibilityManager.isLikeButton()
      ? new ProductLikeButton(this.data, {
          theme: this.styler.theme,
          klass: this.option.hide_info ? `${klass.hide_info} ${klass.like_btn}` : klass.like_btn,
        })
      : null;

    this.is_period_limited = ProductCardHelper.isPeriodLimited(this.data);
    this.is_quantity_limited = ProductCardHelper.isQuantityLimited(this.data);

    this.setBadges();
    this.setTag();

    this.state = {
      img_alt: this.makeImgAlt(),
      post_message: this.makePostMessage(),
      stores_product_id: this.data.stores_product_id,
    };

    this.realtimeTagOption = this.option.realtime_tag
      ? this.option.realtime_tag
      : {
          lang: ET.lang,
          ...this.styler.realtimeTag,
        };

    if (isUndefined(this.data.user_id)) {
      console.warn(`[ProductCardView] this.data.user_id is required.`);
    }
  }

  override template() {
    const style = {
      root: this.option.card_klass || '',
      custom: this.option.klass || '',
      theme: this.styler.klass.theme,
      hidden: this.styler.klass.hidden,
      direction: this.styler.klass.direction,
      sold_out: this.styler.klass.soldOut,
      size: this.styler.klass.cardSize,
    };

    return html`<div
      class="${klass.product_card} ${style.root} ${style.custom} ${style.theme} ${style.direction} ${style.hidden} ${style.size}"
      style="${this.styler.injectedStyles}"
    >
      <a
        class="${klass.thumbnail_container} ${style.sold_out}"
        href="${this.makeLink()}"
        data-post-message="${dataStr(this.state.post_message)}"
      >
        ${this.RankingBadgeView ?? ''} ${this.BadgeListView ?? ''}

        <!-- 상품 썸네일 -->
        ${new ProductThumbnailView({
          thumbnail: this.data.thumbnail_info,
          options: {
            klass: this.option.klass,
            img_alt: this.option.img_alt,
            is_lazy: this.option.is_lazy,
            resize_width: this.option.resize_width,
            without_max_style: this.option.without_max_style,
          },
        })}

        <!-- hide_info: true => 좋아요 버튼 우측 상단에 위치 -->
        ${this.option.hide_info ? (this.LikeButton ?? '') : ''}
      </a>

      ${this.option.hide_info
        ? ''
        : html`<div class="${klass.info_container}">
            <div class="${klass.info}">
              <!-- 실시간 태그 -->
              ${this.visibilityManager.isRealtimeTag() && isDate(this.data.ordered_at) // TODO: isRealtimeTag 타입가드 외않되
                ? html`<div
                    class="${klass.top_tag_wrap} ${classes({
                      [klass.stretch]:
                        this.realtimeTagOption.options &&
                        this.realtimeTagOption.options.includes(ProductTagOption.stretch),
                    })}"
                  >
                    ${ProductTagFactory.create(
                      'realtime',
                      new Date(this.data.ordered_at),
                      this.realtimeTagOption,
                    )}
                  </div>`
                : ''}

              <!-- n명이 보는 중 태그 -->
              ${this.visibilityManager.isWatcherCountTag() && isNumber(this.data.watcher_count)
                ? html`<div class="${klass.top_tag_wrap}">
                    ${ProductTagFactory.create('watcher_count', this.data.watcher_count, {
                      lang: ET.lang,
                      size: this.styler.watcherCountTagSize,
                      radius: ProductTagRadius.none,
                      options: [ProductTagOption.stretch, ProductTagOption.fit_content],
                    })}
                  </div>`
                : ''}

              <div class="${klass.store_name_wrap}">
                <!-- 스토어명 -->
                ${new ProductStoreName(this.data, {
                  klass: this.styler.fontSize.store_name,
                })}

                <!-- hide_info: false => 좋아요 버튼 info에 위치 -->
                ${this.LikeButton ?? ''}
              </div>

              <!-- 상품명 -->
              <span class="${klass.product_name_wrap}">
                ${new ProductCardName(this.data, {
                  klass: this.styler.fontSize.product_name,
                })}
              </span>

              <!-- 가격 -->
              <span class="${klass.price_wrap}">
                ${new ProductCardPrice(
                  { price: this.data.price },
                  {
                    price_klass: this.styler.fontSize.price,
                    currency_klass: this.styler.fontSize.currency,
                  },
                )}
              </span>

              ${this.visibilityManager.isBottomTag() || this.visibilityManager.isReview()
                ? html`<div class="${klass.review_and_tag_container}">
                    <a
                      href="${this.makeLink()}"
                      data-post-message="${dataStr(this.state.post_message)}"
                      class="${klass.review_wrap} ${style.theme}"
                    >
                      <!-- 리뷰 카운트, 평점 -->
                      ${this.visibilityManager.isReview()
                        ? html`
                            <span class="${this.styler.reviewIcon}">${StarIcon}</span>
                            <span class="${this.styler.fontSize.review}">
                              ${this.data.review_score.toFixed(1)}
                            </span>
                            <span class="${this.styler.fontSize.review}">(${this.data.review_count})</span>
                          `
                        : ''}
                    </a>

                    <!-- 하단 태그 -->
                    ${this.visibilityManager.isBottomTag()
                      ? html`<div class="${klass.bottom_tag_wrap}">${this.TagView || ''}</div>`
                      : ''}
                  </div>`
                : ''}
            </div>
          </div>`}
    </div>`;
  }

  public fadeIn() {
    this.option.is_hidden = false;

    this.element().offsetWidth;
    this.element().classList.remove(klass.hidden);
    this.element().classList.add(klass.fade_in);
  }

  private makeLink() {
    return `/${this.data.domain_name}/products/${this.data.id}`;
  }

  private makeImgAlt(): string {
    const { name, store_name } = this.data;
    const kr_suffix = T.lang === 'kr' ? ' 굿즈, 굿즈 판매, 굿즈샵' : '';
    return `${store_name}, ${name}${kr_suffix}`;
  }

  override onRender() {
    if (this.isUpcoming()) {
      const diff_ms = differenceInMilliseconds(this.data.sell_start_at, new Date());
      setTimeout(() => {
        this.setBadges();
        this.setTag();
        this.redraw();
      }, diff_ms + 1000);
    } else if (this.isPeriodLimited() && !this.isSoldOut()) {
      const diff_ms = differenceInMilliseconds(this.data.sell_end_at, new Date());
      setTimeout(() => {
        this.setBadges();
        this.setTag();
        this.redraw();
      }, diff_ms + 1000);
    }

    this.delegate('click', `.${klass.like_btn}`, (e) => {
      e.originalEvent.preventDefault();
      this.dispatchEvent(ProductLikeToggleEvent, {
        bubbles: true,
        detail: {
          toggleLikeButton: this.toggleLikeButton.bind(this),
        },
      });
    });
  }

  public async toggleLikeButton() {
    // TODO: window.box 로직 제거
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!this.data.user_id || window.box.sel('is_user->type') == 'TEMP') {
      const confirmed = (await Confirm.open({
        title: ET('mps2::login::need_login::title'),
        message: preventEscape(ET('mps2::login::need_login::message')),
        confirmText: ET('mps2::login::login'),
        cancelText: ET('mps2::signup::close'),
      })) as boolean;

      if (confirmed) {
        if (MShopUtilF.isApp()) {
          pushLoginStack();
        } else {
          location.href = `/${T.lang}/@/login?url=${location.pathname + location.search}`;
        }
      }
    } else {
      this.data.is_liked = !this.data.is_liked;

      if (this.LikeButton) {
        this.LikeButton.setActivation(this.data.is_liked);
        this.LikeButton.redraw();
      }
      updateLikeMarppleShop(!this.data.is_liked, this.data.stores_product_id);
    }
  }

  private setBadges(): void {
    if (!this.option.hide_badges) {
      this.BadgeListView = ProductBadgeList.forList(
        ProductBadgeVariant.DEFAULT_BADGE,
        {
          ...this.data,
          server_timestamp: Date.now(),
        },
        {
          max_length: 2,
          size: this.styler.badgeSize,
          lang: ET.lang,
          styles: {
            container: klass.badge_container,
          },
        },
      );

      // `Ranking` 랭킹, 주입한 경우에만 표시
      if (this.data.ranking) {
        this.RankingBadgeView = ProductBadgeFactory.create(ProductBadgeType.RANKING, {
          value: this.data.ranking,
          option: {
            klass: klass.ranking_badge,
            size: this.styler.rankBadgeSize,
            lang: ET.lang,
          },
          server_timestamp: Date.now(),
        });
      }
    }
  }

  private setTag(): void {
    if (!this.visibilityManager.isBottomTag()) {
      return;
    }

    const props: ProductTagBaseProps = {
      lang: ET.lang,
      size: this.styler.bottomTagSize,
    };

    if (this.isSoldOut()) {
      this.TagView = ProductTagFactory.create(product_tag.sold_out, props);
      return;
    }

    if (this.isUpcoming()) {
      const value = new Date(this.data.sell_start_at);
      this.TagView = ProductTagFactory.create(product_tag.upcoming, value, props);
      return;
    }

    const quantity_visible = ProductTagHelper.isQuantityLimitedTagVisible(this.data);
    const period_visible = ProductTagHelper.isPeriodLimitedTagVisible(this.data);

    if (quantity_visible) {
      const value = this.data.quantity - this.data.order_count;
      this.TagView = ProductTagFactory.create(product_tag.quantity_limited, value, props);
      return;
    } else if (this.isPeriodLimited() && period_visible) {
      const value = new Date(this.data.sell_end_at);
      this.TagView = ProductTagFactory.create(product_tag.period_limited, value, props);
      return;
    }

    this.TagView = undefined;
  }

  private isUpcoming(): this is ProductCardView & {
    data: { sell_start_at: string; sell_end_at: string };
  } {
    return ProductCardHelper.isBeforeSale(this.data);
  }

  /**
   * 품절인지 아닌지 판별하는 함수
   */
  private isSoldOut(): boolean {
    return ProductCardHelper.isSoldOut(this.data);
  }

  private isPeriodLimited(): this is ProductCardView & {
    data: { sell_start_at: string; sell_end_at: string };
  } {
    return this.is_period_limited;
  }

  override redraw() {
    this.option.is_lazy = false;
    return super.redraw();
  }

  private getProductDetailUrl() {
    return `/${this.data.domain_name}/products/${this.data.id}`;
  }

  private makePostMessage(): PostMessageNavigate {
    return makeSubScreenNavigate(this.getProductDetailUrl());
  }

  public setRealtimeTagOption(option: Partial<ProductTagBaseProps>): this {
    this.realtimeTagOption = {
      ...this.realtimeTagOption,
      ...option,
    };
    return this;
  }
}
